import { useEffect, useRef } from 'react';

const DEFAULT_ACTIVITY_EVENTS: (keyof DocumentEventMap)[] = [
  'mousemove',
  'mousedown',
  'keydown',
  'wheel',
  'touchstart',
  'scroll'
];

interface UseIdleTimerOptions {
  /** Duration of inactivity (ms) after which `onIdle` fires. */
  timeoutMs: number;
  /** Called when the user has been inactive for `timeoutMs`. Keeps firing on every subsequent idle interval. */
  onIdle: () => void;
  /** When `false`, the timer is paused and no activity listeners are attached. */
  enabled?: boolean;
}

/** How often the backup poll re-checks elapsed idle time while the tab is visible. */
const POLL_INTERVAL_MS = 1000;

/**
 * Fires `onIdle` after `timeoutMs` of no mouse/keyboard/scroll/touch activity, then keeps
 * firing again for every further idle interval while still inactive.
 *
 * Idle time is measured from a `Date.now()` timestamp rather than a single `setTimeout`
 * duration, because Chrome throttles/freezes timers in backgrounded or suspended tabs —
 * a duration-based timer fires late and re-arms for a full `timeoutMs` from that late
 * moment, silently extending the real idle window. Re-checking against the timestamp on
 * `visibilitychange` catches a stale session the instant the tab regains focus instead of
 * waiting for a throttled timer to eventually land.
 */
export const useIdleTimer = ({ timeoutMs, onIdle, enabled = true }: UseIdleTimerOptions): void => {
  const onIdleRef = useRef(onIdle);
  const lastActivityRef = useRef(Date.now());

  onIdleRef.current = onIdle;

  useEffect(() => {
    if (!enabled) return undefined;

    lastActivityRef.current = Date.now();

    const markActive = () => {
      lastActivityRef.current = Date.now();
    };

    const checkIdle = () => {
      const idleFor = Date.now() - lastActivityRef.current;

      if (idleFor >= timeoutMs) {
        lastActivityRef.current = Date.now();
        onIdleRef.current();
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') checkIdle();
    };

    const interval = setInterval(checkIdle, POLL_INTERVAL_MS);

    DEFAULT_ACTIVITY_EVENTS.forEach((event) => document.addEventListener(event, markActive));
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', checkIdle);

    return () => {
      clearInterval(interval);
      DEFAULT_ACTIVITY_EVENTS.forEach((event) => document.removeEventListener(event, markActive));
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', checkIdle);
    };
  }, [timeoutMs, enabled]);
};
